import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { useStoreEdit } from "@/graphql/hooks/useStoreEdit"
import { useCountries, useRegions, useLocations } from "@/graphql/hooks/Locations/useLocations"
import { useAllGames } from "@/graphql/hooks/useGames"
import { useAllTournamentTypes } from "@/graphql/hooks/useAllTournamentTypes"
import { Store, Globe, Phone, Mail, MapPin } from "lucide-react"
import { StoreAdminSection, storeLevels } from "./StoreAdminSection"

const storeSchema = z.object({
  name: z.string().min(1, "El nombre es requerido"),
  description: z.string().optional(),
  phone: z.string().min(1, "El teléfono es requerido"),
  email: z.string().email("Email inválido"),
  active: z.boolean(),
  levelId: z.number().optional(),
  clasificationId: z.number().optional(),
  storeGames: z.array(z.number()).optional(),
  tournamentTypes: z
    .array(
      z.object({
        typeId: z.number(),
        quantity: z.number().min(1, "La cantidad debe ser mayor a 0"),
      }),
    )
    .optional(),
  siteUrl: z.string().url("URL inválida").optional().or(z.literal("")),
  facebookUrl: z.string().url("URL inválida").optional().or(z.literal("")),
  instagramUrl: z.string().url("URL inválida").optional().or(z.literal("")),
  youtubeUrl: z.string().url("URL inválida").optional().or(z.literal("")),
  twitterUrl: z.string().url("URL inválida").optional().or(z.literal("")),
  address: z.object({
    name: z.string().min(1, "El nombre de la dirección es requerido"),
    address: z.string().min(1, "La dirección es requerida"),
    countryId: z.number().min(1, "Selecciona un país"),
    regionId: z.number().min(1, "Selecciona una región"),
    locationId: z.number().min(1, "Selecciona una ubicación").optional().or(z.literal(0)),
  }),
})

type StoreFormData = z.infer<typeof storeSchema>

interface StoreEditModalProps {
  store
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export function StoreEditModal({ store, open, onClose, onSuccess }: StoreEditModalProps) {
  const [selectedCountry, setSelectedCountry] = useState<number>(0)
  const [selectedRegion, setSelectedRegion] = useState<number>(0)
  const [tournamentTypeQuantities, setTournamentTypeQuantities] = useState<Record<number, number>>(
    {},
  )

  const { countries } = useCountries()
  const { regions } = useRegions(selectedCountry)
  const { locations, refetchLocations } = useLocations({ regionId: selectedRegion })
  const { editStore, loading } = useStoreEdit()
  const { games } = useAllGames(false)
  const { tournamentTypes } = useAllTournamentTypes()

  const form = useForm<StoreFormData>({
    resolver: zodResolver(storeSchema),
    defaultValues: {
      name: "",
      description: "",
      phone: "",
      email: "",
      active: true,
      levelId: 1,
      clasificationId: 1,
      storeGames: [],
      tournamentTypes: [],
      siteUrl: "",
      facebookUrl: "",
      instagramUrl: "",
      youtubeUrl: "",
      twitterUrl: "",
      address: {
        name: "",
        address: "",
        countryId: 0,
        regionId: 0,
        locationId: 0,
      },
    },
  })

  // Load store data when modal opens
  useEffect(() => {
    if (store && open) {
      // Set tournament type quantities
      const quantities: Record<number, number> = {}
      store.StoreTournamentTypes?.forEach((stt: { typeId: number; quantity: number }) => {
        quantities[stt.typeId] = stt.quantity
      })
      setTournamentTypeQuantities(quantities)

      form.reset({
        name: store.name || "",
        description: store.description || "",
        phone: store.phone || "",
        email: store.email || "",
        active: store.active === 1,
        levelId: store.levelId || 1,
        clasificationId: store.clasificationId || 1,
        storeGames: store.StoreGames?.map((sg: { gameId: number }) => sg.gameId) || [],
        tournamentTypes:
          store.StoreTournamentTypes?.map((stt: { typeId: number; quantity: number }) => ({
            typeId: stt.typeId,
            quantity: stt.quantity,
          })) || [],
        siteUrl: store.siteUrl || "",
        facebookUrl: store.facebookUrl || "",
        instagramUrl: store.instagramUrl || "",
        youtubeUrl: store.youtubeUrl || "",
        twitterUrl: store.twitterUrl || "",
        address: {
          name: store.Address?.name || "",
          address: store.Address?.address || "",
          countryId: store.Address?.countryId || 0,
          regionId: store.Address?.regionId || 0,
          locationId: store.Address?.locationId || 0,
        },
      })

      setSelectedCountry(store.Address?.countryId || 0)
      setSelectedRegion(store.Address?.regionId || 0)
    }
  }, [store, open, form])

  const onSubmit = async (data: StoreFormData) => {
    try {
      const tournamentTypesData = Object.entries(tournamentTypeQuantities)
        .filter(([_, quantity]) => quantity > 0)
        .map(([typeId, quantity]) => ({
          typeId: parseInt(typeId),
          quantity,
        }))

      await editStore({
        id: store.id,
        name: data.name,
        description: data.description,
        phone: data.phone,
        email: data.email,
        active: data.active ? 1 : 0,
        levelId: data.levelId,
        clasificationId: data.clasificationId,
        storeGames: data.storeGames,
        tournamentTypes: tournamentTypesData,
        siteUrl: data.siteUrl,
        facebookUrl: data.facebookUrl,
        instagramUrl: data.instagramUrl,
        youtubeUrl: data.youtubeUrl,
        twitterUrl: data.twitterUrl,
        address: {
          id: store.Address.id,
          name: data.address.name,
          address: data.address.address,
          countryId: data.address.countryId,
          regionId: data.address.regionId,
          locationId: data.address.locationId,
        },
      })
      onSuccess()
    } catch (error) {
      console.error("Error updating store:", error)
    }
  }

  const handleCountryChange = (value: string) => {
    const countryId = parseInt(value)
    setSelectedCountry(countryId)
    setSelectedRegion(0)
    form.setValue("address.countryId", countryId)
    form.setValue("address.regionId", 0)
    form.setValue("address.locationId", 0)
  }

  const handleRegionChange = (value: string) => {
    const regionId = parseInt(value)
    setSelectedRegion(regionId)
    refetchLocations(regionId)
    form.setValue("address.regionId", regionId)
    form.setValue("address.locationId", 0)
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store className="h-5 w-5" />
            Editar Tienda: {store?.name}
          </DialogTitle>
          <DialogDescription>Actualiza la información básica de la tienda.</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre de la tienda</FormLabel>
                    <FormControl>
                      <Input placeholder="Nombre de la tienda" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      Email
                    </FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="email@tienda.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1">
                      <Phone className="h-4 w-4" />
                      Teléfono
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Número de teléfono" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="levelId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nivel de tienda</FormLabel>
                    <Select
                      value={field.value?.toString()}
                      onValueChange={(value) => field.onChange(parseInt(value))}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona un nivel" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {storeLevels.map((level) => (
                          <SelectItem key={level.id} value={level.id.toString()}>
                            {level.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Descripción de la tienda"
                      className="resize-none"
                      rows={5}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Admin Section */}
            <StoreAdminSection
              control={form.control}
              games={games.map((g) => ({ ...g, isActive: g.isActive === 1 }))}
              tournamentTypes={tournamentTypes}
              tournamentTypeQuantities={tournamentTypeQuantities}
              setTournamentTypeQuantities={setTournamentTypeQuantities}
              showLevel={false}
            />

            {/* Social Media URLs */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Enlaces y Redes Sociales
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="siteUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sitio web</FormLabel>
                      <FormControl>
                        <Input placeholder="https://tienda.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="facebookUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Facebook</FormLabel>
                      <FormControl>
                        <Input placeholder="https://facebook.com/tienda" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="instagramUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Instagram</FormLabel>
                      <FormControl>
                        <Input placeholder="https://instagram.com/tienda" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="youtubeUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>YouTube</FormLabel>
                      <FormControl>
                        <Input placeholder="https://youtube.com/tienda" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="twitterUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Twitter</FormLabel>
                      <FormControl>
                        <Input placeholder="https://twitter.com/tienda" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Información de Dirección
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="address.name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nombre de la dirección</FormLabel>
                      <FormControl>
                        <Input placeholder="Ej: Tienda Principal" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address.address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Dirección completa</FormLabel>
                      <FormControl>
                        <Input placeholder="Calle, número, etc." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address.countryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>País</FormLabel>
                      <Select value={field.value?.toString()} onValueChange={handleCountryChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona un país" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {countries?.map((country) => (
                            <SelectItem key={country.id} value={country.id.toString()}>
                              {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address.regionId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Región</FormLabel>
                      <Select
                        value={field.value?.toString()}
                        onValueChange={handleRegionChange}
                        disabled={!selectedCountry}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona una región" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {regions?.map((region) => (
                            <SelectItem key={region.id} value={region.id.toString()}>
                              {region.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address.locationId"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Ubicación</FormLabel>
                      <Select
                        value={field.value?.toString()}
                        onValueChange={(value) => field.onChange(parseInt(value))}
                        disabled={!selectedRegion}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona una ubicación" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {locations?.map((location) => (
                            <SelectItem key={location.id} value={location.id.toString()}>
                              {location.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? <LoadingScreen message="" /> : "Guardar Cambios"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
